import { useEffect, useMemo, useState } from 'react';
import type { BreakSlot, DutySlot } from '../../../services/dutyBoardService';
import { addDaysToDateKey } from '../../../services/payroll/time';
import {
  costRosterDay,
  overtimeWeekFor,
  profitabilityService,
  STATUTORY_TRIGGER_KINDS,
  summarisePayTriggers,
  type PayTriggerSummary,
  type RosteredShift,
  type RosterPay,
} from '../../../services/profitabilityService';

/** Pay rules are read this far either side of the day on screen, so paging through a month costs one load. */
const WINDOW_DAYS = 35;

interface PayFlagsInput {
  siteId: string | null;
  slotDate: string;
  slots: DutySlot[];
  breakSlots: BreakSlot[];
  /** Whether this user holds the payroll permission; without it only statutory flags show, with no amounts. */
  canSeePay: boolean;
}

export interface PayFlags {
  /** null until the pay rules have loaded, and if they cannot be. */
  summary: PayTriggerSummary | null;
  currencyCode: string;
  showAmounts: boolean;
}

interface LoadedPay {
  pay: RosterPay;
  canSeePay: boolean;
}

/**
 * The shifts on the day the board is showing that will cost more than their
 * hours. Priced from the board's own state, so a flag appears as a duty is
 * dragged and before anything is published.
 *
 * Nothing here may break the board: a failed load or a roster the engine
 * cannot read means no flags, not an error.
 */
export function usePayFlags({ siteId, slotDate, slots, breakSlots, canSeePay }: PayFlagsInput): PayFlags {
  const [loaded, setLoaded] = useState<LoadedPay | null>(null);
  const [elsewhere, setElsewhere] = useState<{ key: string; shifts: RosteredShift[] }>({ key: '', shifts: [] });

  const pay = loaded
    && loaded.canSeePay === canSeePay
    && slotDate >= loaded.pay.window.startDate
    && slotDate <= loaded.pay.window.endDate
    ? loaded.pay
    : null;
  const hasPay = pay !== null;

  useEffect(() => {
    if (!siteId || hasPay) return undefined;
    let cancelled = false;

    profitabilityService.loadRosterPay({
      startDate: addDaysToDateKey(slotDate, -WINDOW_DAYS),
      endDate: addDaysToDateKey(slotDate, WINDOW_DAYS),
    }, canSeePay)
      .then((next) => { if (!cancelled) setLoaded({ pay: next, canSeePay }); })
      .catch((error) => { console.warn('Unable to load pay rules for the roster pay flags', error); });

    return () => { cancelled = true; };
  }, [siteId, slotDate, canSeePay, hasPay]);

  const guardKey = useMemo(() => {
    const ids = new Set<string>();
    slots.forEach((slot) => slot.assignments.forEach((assignment) => ids.add(assignment.guard_id)));
    breakSlots.forEach((slot) => slot.assignments.forEach((assignment) => ids.add(assignment.guard_id)));
    return Array.from(ids).sort().join(',');
  }, [slots, breakSlots]);

  // Overtime is judged on a guard's whole week at every site, which the board does not hold.
  const overtimeWeek = pay && canSeePay ? overtimeWeekFor(slotDate, pay) : null;
  const weekStart = overtimeWeek?.startDate ?? '';
  const weekEnd = overtimeWeek?.endDate ?? '';
  const elsewhereKey = siteId && weekStart && guardKey ? `${siteId}|${slotDate}|${guardKey}` : '';

  useEffect(() => {
    if (!elsewhereKey) return undefined;
    let cancelled = false;

    profitabilityService.fetchRoster(weekStart, weekEnd, guardKey.split(','))
      .then((shifts) => { if (!cancelled) setElsewhere({ key: elsewhereKey, shifts }); })
      .catch((error) => {
        console.warn('Unable to load the rest of the week for the roster pay flags', error);
        if (!cancelled) setElsewhere({ key: elsewhereKey, shifts: [] });
      });

    return () => { cancelled = true; };
  }, [elsewhereKey, weekStart, weekEnd, guardKey]);

  const summary = useMemo(() => {
    if (!siteId || !pay || !pay.context.sites.has(siteId)) return null;
    // Until the week arrives the day is priced alone; the flags fill in when it does.
    const otherShifts = elsewhereKey && elsewhere.key === elsewhereKey ? elsewhere.shifts : [];

    try {
      return summarisePayTriggers(
        costRosterDay({ siteId, slotDate, slots, breakSlots }, otherShifts, pay.context),
        canSeePay ? undefined : STATUTORY_TRIGGER_KINDS,
      );
    } catch (error) {
      console.warn('Unable to price the roster day for pay flags', error);
      return null;
    }
  }, [siteId, slotDate, slots, breakSlots, pay, canSeePay, elsewhereKey, elsewhere]);

  return {
    summary,
    currencyCode: pay?.currencyCode ?? 'EUR',
    showAmounts: canSeePay,
  };
}
